import { useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { BiContextProvidedValue, BiData } from '@wix/da-bi/pkg/events';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { EditorContext } from '@wix/da-editor-tiptap/pkg/components/Editor/Context';
import { useEditorDraftActions } from '@wix/da-editor-tiptap/pkg/clientDrafts/hooks';
import { publishComment } from '../redux/actionCreators';
import { useVerification } from '../../VerificationPopup/withVerification';
import { CommentEditorContext } from '../context';
import { useIsCommentEditorSubmittable } from './useIsCommentEditorSubmittable';
import { CommentEditorSource } from '../redux/types';
/**
 * Provides a function that will post a comment!
 *
 * Relies on all the contexts provided by `<CommentEditorProviders />`,
 * so be sure you have it set up first!
 */ export function useCommentPostAction({ messageId, folderId, shouldDeleteMessage, onPost } = {}) {
    const { isBusy, editorId, commentableType, commentableItemId, editorSource, relevantComment } = useContext(CommentEditorContext);
    const { isReady: isEditorReady, getStateForSave: getEditorStateForSave } = useContext(EditorContext);
    const dispatch = useDispatch();
    const { pushVerificationPopup, isVerificationNeeded } = useVerification();
    const { removeEditorDraft } = useEditorDraftActions(editorId);
    const isSubmittable = useIsCommentEditorSubmittable();
    const logBiEvent = useBiLogger();
    const postComment = useCallback(async ()=>{
        if (isBusy || !isSubmittable || !isEditorReady) {
            return;
        }
        if (isVerificationNeeded) {
            pushVerificationPopup('comment');
            return;
        }
        // Clean up draft state in local storage
        removeEditorDraft();
        if (editorSource !== CommentEditorSource.Edit) {
            logBiEvent(BiData({
                evid: 184,
                itemid: typeof commentableItemId === 'string' ? parseInt(commentableItemId) : commentableItemId,
                typeid: commentableType,
                notification_type: BiContextProvidedValue,
                is_comment_thread: BiContextProvidedValue,
                is_reply: editorSource === CommentEditorSource.Reply ? 1 : 0,
                is_sticky: 0
            }));
        }
        dispatch(publishComment({
            editorId,
            typeId: commentableType,
            itemId: commentableItemId,
            rawContentState: JSON.stringify(getEditorStateForSave()),
            editComment: editorSource === CommentEditorSource.Edit ? relevantComment : undefined,
            replyComment: editorSource === CommentEditorSource.Reply ? relevantComment : undefined,
            messageId,
            folderId,
            shouldDeleteMessage
        }));
        onPost && onPost();
    }, [
        dispatch,
        isSubmittable,
        isEditorReady,
        getEditorStateForSave,
        isBusy,
        editorId,
        editorSource,
        relevantComment,
        commentableType,
        commentableItemId,
        pushVerificationPopup,
        isVerificationNeeded,
        removeEditorDraft,
        logBiEvent,
        messageId,
        shouldDeleteMessage,
        folderId,
        onPost
    ]);
    return postComment;
}

//# sourceMappingURL=useCommentPostAction.js.map